import { useState, useEffect } from 'react';
import { Package, Truck, Clock, AlertTriangle, CheckCircle, XCircle, RefreshCw, Plus, Eye, Filter, Search, X, Calendar as CalendarIcon } from 'lucide-react';
import API_URL from '../config/api';
import ShipmentForm from './ShipmentForm';
import './ShipmentTracker.css';

const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ShipmentTracker = ({ embedded = false }) => {
    const [shipments, setShipments] = useState([]);
    const [pendingShipments, setPendingShipments] = useState([]);
    const [overdueShipments, setOverdueShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [filters, setFilters] = useState({
        status: '',
        laboratory_id: '',
        priority: '',
        from_date: toYMD(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        to_date: toYMD(new Date())
    });
    const [laboratories, setLaboratories] = useState([]);
    const [stats, setStats] = useState([]);

    useEffect(() => {
        fetchData();
        fetchLaboratories();
    }, []);

    useEffect(() => {
        if (activeTab === 'all') {
            fetchShipments();
        } else if (activeTab === 'pending') {
            fetchPendingShipments();
        } else if (activeTab === 'overdue') {
            fetchOverdueShipments();
        } else if (activeTab === 'stats') {
            fetchStats();
        }
    }, [activeTab, filters]);

    const fetchData = async () => {
        await Promise.all([
            fetchShipments(),
            fetchPendingShipments(),
            fetchOverdueShipments()
        ]);
    };

    const fetchShipments = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.laboratory_id) params.append('laboratory_id', filters.laboratory_id);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.from_date) params.append('from_date', filters.from_date);
            if (filters.to_date) params.append('to_date', filters.to_date);

            const response = await fetch(`${API_URL}/shipments?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setShipments(data);
            }
        } catch (err) {
            console.error('Error fetching shipments:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingShipments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/shipments/pending`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPendingShipments(data);
            }
        } catch (err) {
            console.error('Error fetching pending shipments:', err);
        }
    };

    const fetchOverdueShipments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/shipments/overdue`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOverdueShipments(data);
            }
        } catch (err) {
            console.error('Error fetching overdue shipments:', err);
        }
    };

    const fetchLaboratories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/laboratories?type=externo`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setLaboratories(data);
            }
        } catch (err) {
            console.error('Error fetching laboratories:', err);
        }
    };

    const fetchStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/shipments/stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const updateShipmentStatus = async (id, status) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/shipments/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status })
            });
            if (response.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'preparando': return <Package size={18} />;
            case 'enviado': return <Truck size={18} />;
            case 'en_transito': return <Truck size={18} className="moving" />;
            case 'entregado': return <CheckCircle size={18} />;
            case 'resultados_recibidos': return <CheckCircle size={18} />;
            case 'cancelado': return <XCircle size={18} />;
            default: return <Clock size={18} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'preparando': return '#6b7280';
            case 'enviado': return '#083677';
            case 'en_transito': return '#8b5cf6';
            case 'entregado': return '#00BBBF';
            case 'resultados_recibidos': return '#00A5A8';
            case 'cancelado': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            'preparando': 'Preparando',
            'enviado': 'Enviado',
            'en_transito': 'En Tránsito',
            'entregado': 'Entregado',
            'resultados_recibidos': 'Resultados Recibidos',
            'cancelado': 'Cancelado'
        };
        return labels[status] || status;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        // Use UTC timezone to ensure the date is displayed exactly as stored (YYYY-MM-DD)
        // without being shifted by the local timezone offset
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    const handleViewDetail = async (shipment) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/shipments/${shipment.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const fullShipment = await response.json();
                setSelectedShipment(fullShipment);
            } else {
                // Fallback to list data
                setSelectedShipment(shipment);
            }
        } catch (err) {
            setSelectedShipment(shipment);
        }
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setSelectedShipment(null);
        fetchData();
    };

    const renderShipmentCard = (shipment) => {
        // Support both new format (study_names, patient_name) and legacy (study_name, patient_first_name)
        const displayStudyNames = shipment.study_names || shipment.study_name || '';
        const displayPatientName = shipment.patient_name ||
            ((shipment.patient_first_name || '') + ' ' + (shipment.patient_last_name || '')).trim();
        const studyCount = parseInt(shipment.study_count) || 1;

        return (
            <div key={shipment.id} className={`shipment-card ${shipment.days_overdue > 0 ? 'overdue' : ''}`}>
                <div className="shipment-header">
                    <div className="shipment-info">
                        <span className="patient-name">
                            {displayPatientName}
                        </span>
                        <span className="study-name">
                            {displayStudyNames}
                            {studyCount > 1 && (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    background: '#e0f2fe',
                                    color: '#0369a1',
                                    fontSize: '11px',
                                    padding: '1px 6px',
                                    borderRadius: '10px',
                                    marginLeft: '6px',
                                    fontWeight: 600
                                }}>
                                    {studyCount} muestras
                                </span>
                            )}
                        </span>
                    </div>
                    <div
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(shipment.status) }}
                    >
                        {getStatusIcon(shipment.status)}
                        <span>{getStatusLabel(shipment.status)}</span>
                    </div>
                </div>

                <div className="shipment-details">
                    <div className="detail-row">
                        <span className="label">Laboratorio:</span>
                        <span className="value">{shipment.laboratory_name}</span>
                    </div>
                    {shipment.courier_name && (
                        <div className="detail-row">
                            <span className="label">Paquetería:</span>
                            <span className="value">{shipment.courier_name}</span>
                        </div>
                    )}
                    {shipment.tracking_number && (
                        <div className="detail-row">
                            <span className="label">Guía:</span>
                            <span className="value tracking">{shipment.tracking_number}</span>
                        </div>
                    )}
                    {shipment.priority === 'urgente' && (
                        <div className="priority-badge urgente">URGENTE</div>
                    )}
                </div>

                <div className="shipment-actions">
                    <button
                        className="btn-action view"
                        onClick={() => handleViewDetail(shipment)}
                        title="Ver detalles"
                    >
                        <Eye size={16} />
                    </button>
                    {shipment.status === 'preparando' && (
                        <div className="action-with-label">
                            <button
                                className="btn-action ship"
                                onClick={() => updateShipmentStatus(shipment.id, 'enviado')}
                                title="Marcar como enviado"
                            >
                                <Truck size={16} />
                            </button>
                            <span className="action-label">Marcar enviado</span>
                        </div>
                    )}
                    {shipment.status === 'enviado' && (
                        <div className="action-with-label">
                            <button
                                className="btn-action transit"
                                onClick={() => updateShipmentStatus(shipment.id, 'en_transito')}
                                title="En tránsito"
                            >
                                <Truck size={16} />
                            </button>
                            <span className="action-label">En tránsito</span>
                        </div>
                    )}
                    {shipment.status === 'en_transito' && (
                        <div className="action-with-label">
                            <button
                                className="btn-action delivered"
                                onClick={() => updateShipmentStatus(shipment.id, 'entregado')}
                                title="Marcar como entregado"
                            >
                                <CheckCircle size={16} />
                            </button>
                            <span className="action-label">Marcar entregado</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const currentShipments = activeTab === 'pending'
        ? pendingShipments
        : activeTab === 'overdue'
            ? overdueShipments
            : shipments;

    // Pagination logic
    const totalPages = Math.ceil(currentShipments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedShipments = currentShipments.slice(startIndex, startIndex + itemsPerPage);

    // Reset to page 1 when tab or filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, filters]);

    const renderGroupedShipments = () => {
        // defined priority check helper
        const isUrgentActive = (s) => {
            const priority = (s.priority || '').toLowerCase().trim();
            const status = (s.status || '').toLowerCase();
            return priority === 'urgente' && !['entregado', 'resultados_recibidos', 'cancelado'].includes(status);
        };

        const urgentActive = paginatedShipments.filter(isUrgentActive);
        const others = paginatedShipments.filter(s => !isUrgentActive(s));

        const items = [];

        // Render Urgent Active First in their own group
        if (urgentActive.length > 0) {
            items.push(
                <div key="header-urgent" className="month-group-header urgent-header">
                    <AlertTriangle size={20} className="text-red-600" style={{ display: 'inline', marginRight: '8px' }} />
                    Prioridad Alta
                </div>
            );
            urgentActive.forEach(shipment => {
                items.push(renderShipmentCard(shipment));
            });
        }

        // Render Others Grouped by Month
        let lastMonth = '';

        others.forEach(shipment => {
            const date = new Date(shipment.created_at || shipment.shipped_at || new Date());
            const monthYear = date.toLocaleString('es-MX', { month: 'long', year: 'numeric' });
            const currentMonth = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);

            if (currentMonth !== lastMonth) {
                items.push(
                    <div key={`header-${currentMonth}-${shipment.id}`} className="month-group-header">
                        {currentMonth}
                    </div>
                );
                lastMonth = currentMonth;
            }
            items.push(renderShipmentCard(shipment));
        });

        return items;
    };

    return (
        <div className={`shipment-tracker ${embedded ? 'embedded' : ''}`}>
            {!embedded && (
                <div className="tracker-header">
                    <div className="header-title">
                        <Package size={28} />
                        <h2>Seguimiento de Envíos</h2>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={fetchData}>
                            <RefreshCw size={18} />
                        </button>
                        <button className="btn-primary" onClick={() => setShowForm(true)}>
                            <Plus size={18} />
                            Nuevo Envío
                        </button>
                    </div>
                </div>
            )}

            {embedded && (
                <div className="embedded-header">
                    <button className="btn-primary" onClick={() => setShowForm(true)}>
                        <Plus size={18} />
                        Nuevo Envío
                    </button>
                    <button className="btn-refresh" onClick={fetchData}>
                        <RefreshCw size={18} />
                    </button>
                </div>
            )}

            <div className="tracker-tabs">
                <button
                    className={`tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                >
                    Todos ({shipments.length})
                </button>
                <button
                    className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pendientes ({pendingShipments.length})
                </button>
                <button
                    className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stats')}
                >
                    Estadísticas
                </button>
            </div>

            {activeTab !== 'stats' && (
                <div className="filters-bar">
                    <div className="filter-group">
                        <label>Estado</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="">Todos los estados</option>
                            <option value="preparando">Preparando</option>
                            <option value="enviado">Enviado</option>
                            <option value="en_transito">En Tránsito</option>
                            <option value="entregado">Entregado</option>
                            <option value="resultados_recibidos">Resultados Recibidos</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Laboratorio</label>
                        <select
                            value={filters.laboratory_id}
                            onChange={(e) => setFilters({ ...filters, laboratory_id: e.target.value })}
                        >
                            <option value="">Todos los laboratorios</option>
                            {laboratories.map(lab => (
                                <option key={lab.id} value={lab.id}>{lab.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Prioridad</label>
                        <select
                            value={filters.priority}
                            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                        >
                            <option value="">Todas las prioridades</option>
                            <option value="normal">Normal</option>
                            <option value="urgente">Urgente</option>
                        </select>
                    </div>
                    <div className="filter-group date-filter">
                        <label>Desde</label>
                        <input
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                        />
                    </div>
                    <div className="filter-group date-filter">
                        <label>Hasta</label>
                        <input
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                        />
                    </div>
                    <button 
                        className="btn-clear-filters" 
                        onClick={() => setFilters({
                            status: '',
                            laboratory_id: '',
                            priority: '',
                            from_date: '',
                            to_date: ''
                        })}
                        title="Limpiar filtros"
                    >
                        <X size={16} />
                        Limpiar
                    </button>
                </div>
            )}

            <div className="tracker-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Cargando envíos...</p>
                    </div>
                ) : activeTab === 'stats' ? (
                    <div className="stats-grid">
                        {stats.length === 0 ? (
                            <p className="no-data">No hay datos de estadísticas disponibles</p>
                        ) : (
                            stats.map(stat => (
                                <div key={stat.laboratory_id} className="stat-card">
                                    <h4>{stat.laboratory_name}</h4>
                                    <div className="stat-row">
                                        <span>Total envíos:</span>
                                        <strong>{stat.total_shipments}</strong>
                                    </div>
                                    <div className="stat-row">
                                        <span>Completados:</span>
                                        <strong className="success">{stat.completed}</strong>
                                    </div>
                                    <div className="stat-row">
                                        <span>Pendientes:</span>
                                        <strong className="warning">{stat.pending}</strong>
                                    </div>
                                    <div className="stat-row">
                                        <span>Retrasados:</span>
                                        <strong className="danger">{stat.overdue}</strong>
                                    </div>
                                    <div className="stat-row">
                                        <span>Tiempo promedio:</span>
                                        <strong>{stat.avg_turnaround_days ? `${Math.round(stat.avg_turnaround_days)} días` : '-'}</strong>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : currentShipments.length === 0 ? (
                    <div className="empty-state">
                        <Package size={64} />
                        <h3>No hay envíos</h3>
                        <p>
                            {activeTab === 'overdue'
                                ? '¡Excelente! No hay envíos retrasados.'
                                : 'Crea un nuevo envío para empezar a rastrear muestras.'}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="shipments-grid">
                            {renderGroupedShipments()}
                        </div>
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    ? Anterior
                                </button>
                                <span className="pagination-info">
                                    Página {currentPage} de {totalPages} ({currentShipments.length} envíos)
                                </span>
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                >
                                    Siguiente ?'
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showForm && (
                <ShipmentForm
                    onClose={() => setShowForm(false)}
                    onSuccess={handleFormSuccess}
                    laboratories={laboratories}
                />
            )}

            {selectedShipment && (
                <div className="shipment-detail-modal" onClick={() => setSelectedShipment(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Detalle del Envío #{selectedShipment.id}</h3>
                            <button onClick={() => setSelectedShipment(null)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Studies list */}
                            <div className="detail-section">
                                <h4>Muestras incluidas ({selectedShipment.studies ? selectedShipment.studies.length : 1})</h4>
                                {selectedShipment.studies && selectedShipment.studies.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {selectedShipment.studies.map((study, idx) => (
                                            <div key={study.study_id || idx} style={{
                                                background: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                padding: '8px 12px',
                                                fontSize: '13px'
                                            }}>
                                                <strong>{study.study_name}</strong>
                                                <span style={{ color: '#64748b', marginLeft: '8px' }}>
                                                    {study.patient_first_name} {study.patient_last_name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>{selectedShipment.study_name || selectedShipment.study_names || '-'}</p>
                                )}
                            </div>
                            <div className="detail-section">
                                <h4>Laboratorio</h4>
                                <p>{selectedShipment.laboratory_name}</p>
                            </div>
                            <div className="detail-section">
                                <h4>Paquetería</h4>
                                <p>{selectedShipment.courier_name || '-'}</p>
                            </div>
                            <div className="detail-section">
                                <h4>Número de Guía</h4>
                                <p>{selectedShipment.tracking_number || '-'}</p>
                            </div>
                            <div className="detail-section">
                                <h4>Estado</h4>
                                <span
                                    className="status-badge"
                                    style={{ backgroundColor: getStatusColor(selectedShipment.status) }}
                                >
                                    {getStatusLabel(selectedShipment.status)}
                                </span>
                            </div>
                            {selectedShipment.notes && (
                                <div className="detail-section">
                                    <h4>Notas</h4>
                                    <p>{selectedShipment.notes}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShipmentTracker;
